/*
 * Copyright © 2023 Medaica, Inc
 *
 * All rights reserved.
 *
 * This code is confidential and proprietary information belonging to Medaica, Inc.
 * Unauthorized copying, distribution, or use of this code, in whole or in part,
 * is strictly prohibited, and may constitute a violation of intellectual property rights.
 *
 * If you have received this code in error, please notify the owner immediately
 * at support@medaica.com and delete this file from your system.
 */

import { auscultationPoints, AuscultationPointType, Sex } from "@medaica/common/const"
import { Transform, Exclude, Type } from "class-transformer"
import { getConfig } from "@medaica/common/services/util"
import { buildAuscultationFileName } from "@medaica/common/services/factories"
import { NoiseQualityRecord } from "../components/audio-player/noise-quality-recorder"

const TransformDate = () => {
  const toPlain = Transform(({ value }) => (value as Date).toString(), {
    toPlainOnly: true,
  })

  const toClass = Transform(
    ({ value }) => {
      if (typeof value === "string") {
        return value ? new Date(value) : null
      } else {
        return value as Date
      }
    },
    {
      toClassOnly: true,
    }
  )

  // eslint-disable-next-line @typescript-eslint/ban-types
  return function (target: Object, key: string) {
    toPlain(target, key)
    toClass(target, key)
  }
}

// Converts a YYYY-MM-DD string from json into a Date object and converts a Date object into YYYY-MM-DD string
const TransformDateNoTime = () => {
  const toDateString = (date: Date | undefined | null): string | null => {
    if (!date) {
      return null
    }
    const day = date.getDate().toString().padStart(2, "0")
    const month = (date.getMonth() + 1).toString().padStart(2, "0")
    const year = date.getFullYear()
    return `${year}-${month}-${day}`
  }

  const toPlain = Transform(({ value }) => toDateString(value as Date), {
    toPlainOnly: true,
  })

  const toClass = Transform(
    ({ value }) => {
      return value ? new Date(`${value} 00:00:00`) : null
    },
    {
      toClassOnly: true,
    }
  )

  // eslint-disable-next-line @typescript-eslint/ban-types
  return function (target: Object, key: string) {
    toPlain(target, key)
    toClass(target, key)
  }
}

const TransformSex = () => {
  const toPlain = Transform(
    ({ value }: { value: Sex | null }) => {
      return value ? value.toString() : null
    },
    {
      toPlainOnly: true,
    }
  )

  const toClass = Transform(
    ({ value }: { value: "male" | "female" | null }) => {
      return value ? Sex[value] : null
    },
    {
      toClassOnly: true,
    }
  )

  // eslint-disable-next-line @typescript-eslint/ban-types
  return function (target: Object, key: string) {
    toPlain(target, key)
    toClass(target, key)
  }
}

const TransformAuscultationPoint = () => {
  const toPlain = Transform(
    ({ value }: { value: AuscultationPoint }) => {
      return value.label
    },
    {
      toPlainOnly: true,
    }
  )

  const toClass = Transform(
    ({ value }: { value: "string" }) => {
      return auscultationPoints.getByLabel(value)
    },
    {
      toClassOnly: true,
    }
  )

  // eslint-disable-next-line @typescript-eslint/ban-types
  return function (target: Object, key: string) {
    toPlain(target, key)
    toClass(target, key)
  }
}

type MedaicaApiAccessToken = {
  expiresAt: Date
  value: string
}

type FirebaseAccessToken = {
  value: string
  expires_in: Date
}

type TwilioAccessToken = {
  value: string
  expires_in: Date
}

type TokenData = {
  value: string
  tokenType: string
  expiresIn: number
  userId: string
}

type SelfExamAccessTokenData = {
  selfExamRequestId: string
} & TokenData

type LiveExamAnonAccessToken = {
  value: string
  tokenType: string
  expiresAt: Date
  userId: string
  userName: string
  liveExamId: string
}

// --- D2C booking wizard (05 §2 / contract 03 §3) ---
// Plain DTOs: the response interceptor (convertDateStringsToDates) coerces ISO
// date strings to Date, so no class-transformer decorators are needed here.
// Field casing is camelCase per the repo/APIModel convention (re-verify against
// real M4 — see INTEGRATION-NOTES).

// createSession → masked email + code expiry + an opaque verificationToken that
// verify/resend-code carry in BOTH the ?t= and tokenless paths (proposed 03 §3
// addition, user-approved — pending a plan-repo update).
type BookingSession = {
  maskedEmail: string
  codeExpiresAt: Date
  verificationToken: string
}

type BookingVerifyResult = {
  accessToken: string
  expiresIn: number
}

// Patient report download page (app/patient_report/schemas.py): the gate
// responses share the booking gate's shape; the facts are the download
// screen's header only — nothing from the report body.
type PatientReportSession = {
  maskedEmail: string
  codeExpiresAt: Date
  verificationToken: string
}

type PatientReportVerifyResult = {
  accessToken: string
  expiresIn: number
}

type PatientReportFacts = {
  patientName: string | null
  examDate: string | null
  releasedAt: Date
  filename: string
  // The clinician-format copy of the report the patient can pass on to their own doctor;
  // null when the release carries none.
  providerReport: { filename: string } | null
}

type BookingAppointment = {
  examRef: string
  patientName: string
  slotStart: Date
  timeZone: string
  status: "scheduled" | "completed" | "no_show" | "cancelled"
  videoRoomUrl: string | null
}

type BookingAccount = {
  // Nullable on the wire (backend Optional[str] — a buyer row can have no name).
  buyerFirstName: string | null
  // Buyer contact fields used to prefill the patient form on the self-booking ("me") path.
  // Returned only on the verified (non-decoy) account; optional so the client degrades
  // gracefully before the backend ships them, and null-safe for a decoy session.
  buyerLastName?: string | null
  buyerEmail?: string | null
  buyerPhone?: string | null
  examsAvailable: number
  earliestExpiry: Date | null
  appointments: BookingAppointment[]
}

type BookingSlot = {
  startUtc: Date
  capacity: number
}

// Booking window/exam constants — served by GET /booking/config so the wizard is a single
// source of truth with the backend slot engine (lead/horizon/slot sizing) rather than drifting.
type BookingConfig = {
  examMinutes: number
  slotMinutes: number
  minLeadHours: number
  horizonWeeks: number
}

type QuestionnaireQuestion = {
  key: string
  section?: string
  text: string
  kind: "choice" | "multi" | "text" | "number"
  // `value` = stable snake_case id stored in answers; `label` = patient-facing text.
  // v2 multi flags: `exclusive` stands alone (picking it clears every other choice);
  // `other` marks the option whose free-text companion follows via show_if.
  options?: { value: string; label: string; other?: boolean; exclusive?: boolean }[]
  // Render only when a prior (single-select) answer is in the allow-list. snake_case
  // mirrors the served `questionnaire_definition` JSONB (02 §10) — not transformed.
  show_if?: { key: string; in: string[] }
  optional?: boolean
  // kind "number" metadata (v2 height/weight): unit suffix + inclusive whole-number range.
  unit?: string
  min?: number
  max?: number
}

type QuestionnaireDefinition = {
  version: number
  // Patient-facing header paragraphs, rendered above the questions on the health step.
  intro?: string[]
  questions: QuestionnaireQuestion[]
}

type BookingPatientInput = {
  firstName: string
  lastName: string
  dateOfBirth: string
  gender: string
  email: string
  phone?: string
}

type BookingConsentInput = {
  consentType: "telehealth" | "privacy_notice" | "appointment_emails"
  granted: boolean
  policyVersion: number
}

type BookingAnswers = Record<string, string | string[]>

type BookingRequest = {
  requestId: string
  patient: BookingPatientInput
  consents: BookingConsentInput[]
  answers?: BookingAnswers
  slotStart: Date
  timeZone: string
  // False when booking for someone else — the backend records the consent as
  // buyer-attested (M4 `booked_for_me`; defaults true server-side, so send it).
  bookedForMe: boolean
}

type BookingResult = {
  examRef: string
  slotStart: Date
  timeZone: string
  joinNote: string
  examsRemaining: number
}

type Page<T> = {
  page?: number // 1 based, so 1 is the first page, not 0.
  pageSize?: number
  totalCount?: number
  items: T[]
}

class BookedPatientName {
  firstName: string | null
  lastName: string | null

  get fullName(): string {
    return [this.firstName, this.lastName].filter(Boolean).join(" ")
  }
}

/**
 * The booked patient's details, shown only to the technician assigned to the
 * booked exam, and when that technician confirmed the patient's identity.
 */
class BookedPatient extends BookedPatientName {
  @TransformDateNoTime()
  birthdate: Date | null
  phoneNumber: string | null
  @TransformDate()
  identityAffirmedAt: Date | null
}

class VirtualExam {
  @Exclude({ toPlainOnly: true })
  id: string
  @TransformDate()
  @Exclude({ toPlainOnly: true })
  dateCreated: string
  @Type(() => HealthcareProvider)
  healthcareProvider: HealthcareProvider
  @Type(() => PatientProfile)
  patientProfile: PatientProfile
  @TransformDate()
  scheduledStartDate: Date
  @TransformDate()
  scheduledEndDate: Date
  examId: string
  @Transform(({ value }: { value: string }) => `${value.slice(0, 3)}-${value.slice(3, 6)}-${value.slice(6)}`)
  accessCode: string
  @Type(() => Exam)
  exam: Exam
  /**
   * The name the patient was booked under, for a booked exam. Booking reuses
   * a profile by email address, so the shared profile's name can be someone
   * else's. Null when the exam was not booked.
   */
  @Type(() => BookedPatientName)
  bookedPatientName: BookedPatientName | null

  get patientFullName(): string | undefined {
    return this.bookedPatientName?.fullName || this.patientProfile?.fullName
  }

  get link(): string {
    return `${getConfig("PATIENT_PORTAL_URL")}/live-exam/${this.id}`
  }
}

class PatientProfile {
  @Exclude({ toPlainOnly: true })
  id: string
  firstName: string
  lastName: string
  @TransformDate()
  @Exclude({ toPlainOnly: true })
  // todo convert to date
  dateCreated: string
  emailAddress: string
  @TransformDateNoTime()
  birthdate: Date | null
  @TransformSex()
  sex: Sex | null
  healthcareProviderId: string
  organizationId: string
  healthcareProvider: HealthcareProvider

  get fullName(): string {
    return `${this.firstName} ${this.lastName}`
  }
}

class SelfExamRequest {
  id: string
  patientProfileId: string
  @Type(() => PatientProfile)
  patientProfile: PatientProfile
  examDate: Date
  dateCreated: Date
  instructions: string
  @Type(() => SelfExamRequestAuscultationRequest)
  auscultationRequests: SelfExamRequestAuscultationRequest[]
  healthcareProviderId: string
  url: string
  examId?: string
  @Type(() => Exam)
  exam?: Exam
}

class SelfExamRequestAuscultationRequest {
  id: string
  @TransformAuscultationPoint()
  auscultationPoint: AuscultationPoint
  @Type(() => Auscultation)
  auscultation: Auscultation | null
}

class HealthcareProvider {
  id: string
  emailAddress: string
  firstName: string
  lastName: string
  displayName: string
  mustChangePassword: boolean
  tcVersion: number

  get fullName(): string {
    return `${this.firstName} ${this.lastName}`
  }
}

class HealthcareProviderOverview {
  id: string
  emailAddress: string
  firstName: string
  lastName: string
  displayName: string
  roles: string[]
  mustChangePassword: boolean
  tcVersion: number
  totalExams: number
  totalPatients: number
  totalUnreviewedExams: number

  get fullName(): string {
    return `${this.firstName ?? ""} ${this.lastName ?? ""}`
  }
}

type Organization = {
  id: string
  name: string
}

class HealthcareProviderInvitation {
  id: string
  emailAddress: string
  used: boolean
  active: boolean
  dateExpires: Date
  organization: Organization

  get isValid(): boolean {
    return !(this.used || !this.active || this.dateExpires < new Date())
  }
}

class Patient {
  id: string
  firstName: string
  lastName: string
  mustChangePassword: boolean
  tcVersion: number

  get fullName(): string {
    return `${this.firstName} ${this.lastName}`
  }
}

type RegistrationResponse = {
  id: string
  auth0UserId: string
  email: string
}

type BeatAwareReportStatistic = {
  "Chance of Abnormality (%)"?: number
  "Chance of Murmur (%)"?: number
  "S1:S2 Intensity Ratio"?: number
  "Systolic:Diastolic Duration Ratio"?: number
  "S1 Duration (ms)"?: string
  "S2 Duration (ms)"?: string
  "Systole Duration (ms)"?: string
  "Diastole Duration (ms)"?: string
  "Cardiac Cycle (ms)"?: string
  "Heart Rate (BPM)"?: number
  [k: string]: unknown
}

type DurationMeasurementKey =
  | "S1 Duration (ms)"
  | "S2 Duration (ms)"
  | "Systole Duration (ms)"
  | "Diastole Duration (ms)"
  | "Cardiac Cycle (ms)"

type DurationMeasurementAggregate = {
  label: DurationMeasurementKey
  average: number
  min: number
  max: number
  variation: number
}

type CumulativeBeatAwareStats = {
  totalBeats: number
  heartRate: { average: number; min: number; max: number }
  abnormality: { average: number; min: number; max: number }
  murmur: { average: number; min: number; max: number }
  intensityRatio: { average: number; min: number; max: number }
  durationRatio: { average: number; min: number; max: number }
  durations: DurationMeasurementAggregate[]
}

type BeatAwareReportSchema = {
  reportMarkdown: string
  statistics: BeatAwareReportStatistic[]
}

type BeatAwareReportStatus = "processing" | "ready" | "failed" | "error"

class Auscultation {
  id: string
  @TransformDate()
  dateCreated: Date
  examId: string
  deviceLabel: string
  @TransformAuscultationPoint()
  auscultationPoint: AuscultationPoint
  // Null when the recording's upload failed and nothing was stored (older data; the API now rejects such uploads)
  recording: Recording | null
  noiseQualityRecord: NoiseQualityRecord
  heartrateBpm: number | null
  heartbeatPresent: boolean | null
  heartbeatNormal: boolean | null
  ausculthingDiagnosis: number | null
  beatawareId: string | null
  beatawareStatus: BeatAwareReportStatus | null
  beatawareReport: BeatAwareReportSchema | null

  get fileName(): string {
    return buildAuscultationFileName(this.auscultationPoint, this.dateCreated, this.recording?.mimeType ?? "audio/wav")
  }
}

type Recording = {
  id: string
  dateCreated: Date
  auscultationId: string
  duration: number
  mimeType: string
  fileName: string
  data?: Blob
}

enum ExamType {
  Live = "Live",
  SelfHealthcareProviderInitiated = "Self - Provider Initiated",
  SelfPatientInitiated = "Self - Patient Initiated",
}

type TurnCredential = { url: string; urls: string; credential?: string; username?: string }

/**
 * The patient an exam was for, as the API records it per exam: the facts
 * frozen when the exam was booked, or the profile's for an exam that was not
 * booked. Booking reuses a profile by email address, so the shared profile
 * can describe someone else — this is what to show wherever the exam or its
 * Heart Report names the patient; `patientProfile` stays the record to link
 * to and edit.
 */
class ExamPatient {
  firstName: string | null
  lastName: string | null
  emailAddress: string | null
  @TransformDateNoTime()
  birthdate: Date | null
  @TransformSex()
  sex: Sex | null

  get fullName(): string {
    return [this.firstName, this.lastName].filter(Boolean).join(" ")
  }
}

class Exam {
  id: string
  patientId: string | null
  dateCreated: Date
  @Type(() => Auscultation)
  auscultations: Auscultation[] = []
  @Type(() => PatientProfile)
  patientProfile: PatientProfile
  /** Sent whenever the exam was booked or `patientProfile` was loaded with it. */
  @Type(() => ExamPatient)
  examPatient: ExamPatient | null
  patient: Patient
  healthcareProvider: HealthcareProvider
  notes: string
  enhancedNotes: string[]
  reviewed: boolean
  type: ExamType

  get patientFullName(): string {
    if (this.examPatient?.fullName) {
      return this.examPatient.fullName
    }
    return this.patientProfile ? this.patientProfile.fullName : this.patient.fullName
  }
}

/**
 * The patient to show for an exam: its `examPatient`, or the profile when
 * the response carried none (a load without the profile, or an exam with no
 * patient at all is not expected here).
 */
const examPatientOf = (exam: Exam): ExamPatient | PatientProfile => exam.examPatient ?? exam.patientProfile

type AuscultationPoint = {
  label: string
  longLabel: string
  description: string
  type: AuscultationPointType
}

class ExamPrevNextIds {
  prevId: string | null
  nextId: string | null
  totalExams: number
  examSequenceNumber: number
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyError = any

export type {
  RegistrationResponse,
  MedaicaApiAccessToken,
  FirebaseAccessToken,
  TwilioAccessToken,
  LiveExamAnonAccessToken,
  SelfExamAccessTokenData,
  Page,
  BookingSession,
  BookingVerifyResult,
  PatientReportSession,
  PatientReportVerifyResult,
  PatientReportFacts,
  BookingAccount,
  BookingAppointment,
  BookingSlot,
  BookingConfig,
  QuestionnaireDefinition,
  QuestionnaireQuestion,
  BookingPatientInput,
  BookingConsentInput,
  BookingAnswers,
  BookingRequest,
  BookingResult,
  Recording,
  TurnCredential,
  AnyError,
  Organization,
  AuscultationPoint,
  BeatAwareReportStatistic,
  BeatAwareReportSchema,
  BeatAwareReportStatus,
  CumulativeBeatAwareStats,
  DurationMeasurementAggregate,
  DurationMeasurementKey,
}

export {
  Patient,
  HealthcareProvider,
  HealthcareProviderOverview,
  VirtualExam,
  BookedPatientName,
  BookedPatient,
  PatientProfile,
  Exam,
  ExamPatient,
  examPatientOf,
  Auscultation,
  SelfExamRequest,
  SelfExamRequestAuscultationRequest,
  HealthcareProviderInvitation,
  ExamPrevNextIds,
}
